<template>
  <section class="devices-page">
    <header class="page-header">
      <div>
        <h1>Dispositivos</h1>
        <p>Acompanhe a conectividade, o sinal e o retorno dos devices.</p>
      </div>
      <div class="header-actions">
        <el-tooltip
          :disabled="canConfigurePolicy"
          content="Selecione uma empresa para configurar os critérios"
          placement="bottom"
        >
          <span>
            <el-button
              :disabled="!canConfigurePolicy"
              class="policy-button"
              @click="openPolicyDialog"
            >
              <el-icon><Setting /></el-icon>
              Configurar critérios
            </el-button>
          </span>
        </el-tooltip>
      </div>
    </header>

    <div class="toolbar">
      <el-input
        v-model="search"
        clearable
        placeholder="Buscar por Device ID"
        class="search-input"
        @keyup.enter="applySearch"
        @clear="applySearch"
      >
        <template #prefix><el-icon><Search /></el-icon></template>
      </el-input>

      <el-select
        v-if="showBusinessClientFilter"
        v-model="selectedClient"
        clearable
        placeholder="Todas as empresas"
        class="client-filter"
        @change="handleFilterChange"
      >
        <el-option label="Todas as empresas" value="" />
        <el-option
          v-for="client in availableClients"
          :key="client"
          :label="client"
          :value="client"
        />
      </el-select>

      <div class="legend" aria-label="Legenda dos status">
        <span><i class="status-dot online"></i>Online</span>
        <span><i class="status-dot warning"></i>Atenção</span>
        <span><i class="status-dot offline"></i>Offline</span>
      </div>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      class="page-alert"
    />

    <div v-if="loading && devices.length === 0" class="devices-grid">
      <el-skeleton v-for="index in 6" :key="index" animated class="skeleton-card">
        <template #template>
          <el-skeleton-item variant="h3" style="width: 72%" />
          <el-skeleton-item variant="text" style="width: 44%; margin-top: 20px" />
          <el-skeleton-item variant="text" style="width: 90%; margin-top: 28px" />
          <el-skeleton-item variant="text" style="width: 68%; margin-top: 12px" />
        </template>
      </el-skeleton>
    </div>

    <el-empty
      v-else-if="!loading && devices.length === 0"
      description="Nenhum dispositivo encontrado."
    />

    <div v-else class="devices-grid" aria-live="polite">
      <button
        v-for="device in devices"
        :key="device.deviceID"
        type="button"
        class="device-card"
        :class="`device-${device.health_status}`"
        @click="openDevice(device)"
      >
        <span class="card-accent"></span>
        <span class="card-header">
          <span class="device-icon"><font-awesome-icon icon="satellite-dish" /></span>
          <span class="device-heading">
            <strong :title="device.deviceID">{{ device.deviceID }}</strong>
            <small v-if="device.business_client">{{ device.business_client }}</small>
            <small v-else>Empresa não identificada</small>
          </span>
          <span class="health-badge" :class="device.health_status">
            <i class="status-dot" :class="device.health_status"></i>
            {{ healthLabel(device.health_status) }}
          </span>
        </span>

        <span class="card-body">
          <span class="metric-row">
            <span class="metric-label">Último retorno</span>
            <strong>{{ formatDate(device.last_report_at) }}</strong>
          </span>
          <span class="metric-row">
            <span class="metric-label">Conexão</span>
            <strong>{{ device.connected ? "Conectado" : "Desconectado" }}</strong>
          </span>
          <span class="telemetry-row">
            <span><font-awesome-icon icon="signal" /> {{ signalLabel(device) }}</span>
            <span><font-awesome-icon icon="download" /> {{ downloadLabel(device) }}</span>
          </span>
        </span>

        <span class="card-footer">
          <span>{{ device.status?.version || "Versão não informada" }}</span>
          <strong>Ver histórico <font-awesome-icon icon="chevron-right" /></strong>
        </span>
      </button>
    </div>

    <div v-if="total > 0" class="pagination-row">
      <span>{{ devices.length }} de {{ total }} dispositivos</span>
      <el-pagination
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        layout="prev, pager, next"
        background
        @current-change="handlePageChange"
      />
    </div>

    <el-drawer
      v-model="drawerOpen"
      :size="drawerSize"
      destroy-on-close
      class="device-drawer"
      @closed="closeDevice"
    >
      <template #header>
        <div v-if="selectedDevice" class="drawer-title">
          <span class="device-icon"><font-awesome-icon icon="satellite-dish" /></span>
          <div>
            <h2>{{ selectedDevice.deviceID }}</h2>
            <span>{{ selectedDevice.business_client || "Empresa não identificada" }}</span>
          </div>
          <span class="health-badge" :class="selectedDevice.health_status">
            <i class="status-dot" :class="selectedDevice.health_status"></i>
            {{ healthLabel(selectedDevice.health_status) }}
          </span>
        </div>
      </template>

      <div v-if="selectedDevice" class="drawer-content">
        <div class="overview-grid">
          <div>
            <span>Último retorno</span>
            <strong>{{ formatDate(selectedDevice.last_report_at) }}</strong>
          </div>
          <div>
            <span>Conexão</span>
            <strong>{{ selectedDevice.connected ? "Conectado" : "Desconectado" }}</strong>
          </div>
          <div>
            <span>Versão</span>
            <strong>{{ selectedDevice.status?.version || "—" }}</strong>
          </div>
        </div>

        <div class="applied-policy">
          <font-awesome-icon icon="clock" />
          <span>
            Critério aplicado:
            <strong>{{ policyDescription(selectedDevice.health_policy) }}</strong>
          </span>
          <el-tag v-if="selectedDevice.health_policy?.is_default" size="small" effect="plain">
            Padrão
          </el-tag>
        </div>

        <div v-if="selectedDevice.last_event" class="last-event-summary">
          <div>
            <span>Evento mais recente</span>
            <strong>{{ historyEventLabel(selectedDevice.last_event) }}</strong>
          </div>
          <div>
            <span>Ocorrido em</span>
            <strong>{{ formatDate(selectedDevice.last_event.timestamp) }}</strong>
          </div>
          <p v-if="historyMessageLabel(selectedDevice.last_event) !== '—'">
            {{ historyMessageLabel(selectedDevice.last_event) }}
          </p>
        </div>

        <div class="detail-sections">
          <article class="detail-panel">
            <h3><font-awesome-icon icon="signal" /> Sinal</h3>
            <dl>
              <div
                v-for="item in signalDetails(selectedDevice)"
                :key="item.label"
                class="detail-row"
              >
                <dt>{{ item.label }}</dt><dd>{{ item.value }}</dd>
              </div>
            </dl>
          </article>
          <article class="detail-panel">
            <h3><font-awesome-icon icon="download" /> DSM-CC</h3>
            <dl>
              <div
                v-for="item in dsmccDetails(selectedDevice)"
                :key="item.label"
                class="detail-row"
              >
                <dt>{{ item.label }}</dt><dd>{{ item.value }}</dd>
              </div>
            </dl>
          </article>
        </div>

        <el-collapse class="raw-status">
          <el-collapse-item title="Visualizar status completo" name="raw-status">
            <pre>{{ formatJson(selectedDevice.status) }}</pre>
          </el-collapse-item>
        </el-collapse>

        <div class="history-header">
          <div>
            <h3>Histórico do dispositivo</h3>
            <p>Status e eventos recebidos do mais recente para o mais antigo.</p>
          </div>
          <div class="history-actions">
            <el-button
              :loading="historyExporting === 'xlsx'"
              :disabled="historyTotal === 0 || Boolean(historyExporting)"
              @click="exportHistory('xlsx')"
            >
              <font-awesome-icon icon="file-excel" />
              Exportar Excel
            </el-button>
            <el-button
              :loading="historyExporting === 'pdf'"
              :disabled="historyTotal === 0 || Boolean(historyExporting)"
              @click="exportHistory('pdf')"
            >
              <font-awesome-icon icon="file-pdf" />
              Exportar PDF
            </el-button>
          </div>
        </div>

        <div class="history-toolbar">
          <div class="history-filter-group">
            <span class="history-filter-label">Filtros rápidos</span>
            <div class="history-date-shortcuts">
              <button
                v-for="shortcut in HISTORY_DATE_SHORTCUTS"
                :key="shortcut.key"
                type="button"
                class="history-shortcut-btn"
                :class="{ 'history-shortcut-btn--active': activeHistoryShortcut === shortcut.key }"
                @click="applyHistoryShortcut(shortcut.key)"
              >
                {{ shortcut.label }}
              </button>
            </div>
          </div>
          <div class="history-filter-group">
            <span class="history-filter-label">Período</span>
            <el-date-picker
              v-model="historyPeriod"
              type="daterange"
              value-format="YYYY-MM-DD"
              range-separator="até"
              start-placeholder="Data inicial"
              end-placeholder="Data final"
              :editable="false"
              unlink-panels
              clearable
              @change="handleHistoryPeriodChange"
            />
          </div>
          <button
            v-if="historyPeriod?.length === 2"
            type="button"
            class="history-clear-filter"
            @click="clearHistoryPeriod"
          >
            Limpar filtro
          </button>
          <span class="history-filter-help">O período também será aplicado às exportações.</span>
        </div>

        <el-skeleton v-if="historyLoading && history.length === 0" :rows="5" animated />
        <el-empty
          v-else-if="!historyLoading && history.length === 0"
          description="Nenhum histórico encontrado."
          :image-size="80"
        />
        <el-table v-else :data="history" class="history-table" row-key="id">
          <el-table-column label="Recebido em" width="175">
            <template #default="{ row }">{{ formatDate(row.created_at) }}</template>
          </el-table-column>
          <el-table-column label="Tipo" width="90">
            <template #default="{ row }">
              <el-tag
                size="small"
                effect="plain"
                :type="historyIsEvent(row) ? 'warning' : 'info'"
              >
                {{ historyRecordTypeLabel(row) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Conexão" width="130">
            <template #default="{ row }">
              <span
                v-if="historyConnectionState(row) !== null"
                class="connection-label"
                :class="historyConnectionState(row) ? 'connected' : 'disconnected'"
              >
                <i
                  class="status-dot"
                  :class="historyConnectionState(row) ? 'online' : 'offline'"
                ></i>
                {{ historyConnectionLabel(row) }}
              </span>
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column label="Sinal" width="125">
            <template #default="{ row }">{{ historySignalLabel(row) }}</template>
          </el-table-column>
          <el-table-column label="Evento" min-width="170">
            <template #default="{ row }">{{ historyEventLabel(row) }}</template>
          </el-table-column>
          <el-table-column label="Mensagem" min-width="210">
            <template #default="{ row }">{{ historyMessageLabel(row) }}</template>
          </el-table-column>
          <el-table-column label="Corpo" width="115" fixed="right">
            <template #default="{ row }">
              <el-button link type="primary" @click="openHistoryBody(row)">Ver corpo</el-button>
            </template>
          </el-table-column>
        </el-table>

        <el-dialog v-model="historyBodyOpen" title="Corpo do retorno" width="min(760px, 90vw)" append-to-body>
          <pre class="history-body-json">{{ formatJson(selectedHistoryBody) }}</pre>
        </el-dialog>

        <div v-if="historyTotal > 0" class="history-pagination">
          <span>{{ history.length }} de {{ historyTotal }} registros</span>
          <el-pagination
            :current-page="historyPage"
            :page-size="historyPageSize"
            :total="historyTotal"
            layout="prev, pager, next"
            small
            background
            @current-change="handleHistoryPageChange"
          />
        </div>
      </div>
    </el-drawer>

    <el-dialog
      v-model="policyDialogOpen"
      :title="`Critérios de status · ${policyTargetBusinessClient || ''}`"
      width="min(560px, 94vw)"
      destroy-on-close
    >
      <el-skeleton v-if="policyLoading" :rows="4" animated />
      <el-form v-else label-position="top" class="policy-form" @submit.prevent>
        <p class="policy-intro">
          A configuração vale para todos os usuários e dispositivos desta empresa.
          A cor considera somente o intervalo desde o último retorno.
        </p>

        <el-form-item label="Considerar online por">
          <div class="threshold-control">
            <el-input-number
              v-model="policyForm.online_threshold.value"
              :min="1"
              :precision="0"
              controls-position="right"
            />
            <el-select v-model="policyForm.online_threshold.unit">
              <el-option
                v-for="unit in thresholdUnits"
                :key="unit.value"
                :label="unit.label"
                :value="unit.value"
              />
            </el-select>
          </div>
        </el-form-item>

        <el-form-item label="Considerar offline após">
          <div class="threshold-control">
            <el-input-number
              v-model="policyForm.offline_threshold.value"
              :min="1"
              :precision="0"
              controls-position="right"
            />
            <el-select v-model="policyForm.offline_threshold.unit">
              <el-option
                v-for="unit in thresholdUnits"
                :key="unit.value"
                :label="unit.label"
                :value="unit.value"
              />
            </el-select>
          </div>
        </el-form-item>

        <el-alert
          :title="policyPreview"
          :type="policyFormIsValid ? 'info' : 'error'"
          show-icon
          :closable="false"
        />
      </el-form>

      <template #footer>
        <div class="policy-footer">
          <el-button
            v-if="!policyIsDefault"
            text
            type="danger"
            :loading="policySaving"
            @click="resetPolicy"
          >
            Restaurar padrão
          </el-button>
          <span class="policy-footer-actions">
            <el-button @click="policyDialogOpen = false">Cancelar</el-button>
            <el-button
              type="primary"
              :loading="policySaving"
              :disabled="!policyFormIsValid"
              @click="savePolicy"
            >
              Salvar critérios
            </el-button>
          </span>
        </div>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import axios from "axios";
import { ElMessageBox } from "element-plus";
import { Search, Setting } from "@element-plus/icons-vue";
import { config } from "../../config";
import { loadBusinessClients } from "../../services/businessClients";
import { clientViewState } from "../../utils/clientView";
import {
  exportDeviceHistoryExcel,
  exportDeviceHistoryPdf,
} from "../../utils/deviceHistoryExport";
import {
  historyConnectionLabel as connectionLabelForHistory,
  historyConnectionState as connectionStateForHistory,
  historyEventLabel as eventLabelForHistory,
  historyMessageLabel as messageLabelForHistory,
  historyRecordTypeLabel as recordTypeLabelForHistory,
  historySignalLabel as signalLabelForHistory,
  isHistoryEvent,
} from "../../utils/deviceHistoryPresentation";

const HISTORY_DATE_SHORTCUTS = [
  { key: "today", label: "Hoje" },
  { key: "month", label: "Mês atual" },
  { key: "last30", label: "Últimos 30 dias" },
  { key: "year", label: "Ano atual" },
];

function localDateOnly(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default {
  name: "DevicesPage",
  components: { Search, Setting },
  data() {
    return {
      devices: [],
      loading: false,
      error: null,
      search: "",
      appliedSearch: "",
      availableClients: [],
      selectedClient: "",
      clientViewState,
      page: 1,
      pageSize: 24,
      total: 0,
      drawerOpen: false,
      selectedDevice: null,
      history: [],
      historyLoading: false,
      historyTotal: 0,
      historyPage: 1,
      historyPageSize: 20,
      historyExporting: null,
      historyPeriod: [],
      historyBodyOpen: false,
      selectedHistoryBody: null,
      viewportWidth: window.innerWidth,
      refreshTimer: null,
      policyDialogOpen: false,
      policyLoading: false,
      policySaving: false,
      policyIsDefault: true,
      policyForm: {
        online_threshold: { value: 1, unit: "hour" },
        offline_threshold: { value: 1, unit: "day" },
      },
      thresholdUnits: [
        { value: "minute", label: "Minuto(s)" },
        { value: "hour", label: "Hora(s)" },
        { value: "day", label: "Dia(s)" },
      ],
    };
  },
  computed: {
    showBusinessClientFilter() {
      return this.availableClients.length > 1 && !this.clientViewState.selectedClient;
    },
    activeBusinessClient() {
      return this.clientViewState.selectedClient || this.selectedClient || null;
    },
    policyTargetBusinessClient() {
      return this.clientViewState.selectedClient
        || this.selectedClient
        || (this.availableClients.length === 1 ? this.availableClients[0] : null);
    },
    canConfigurePolicy() {
      return Boolean(this.policyTargetBusinessClient);
    },
    drawerSize() {
      return Math.min(780, Math.floor(this.viewportWidth * 0.94));
    },
    activeHistoryShortcut() {
      if (!Array.isArray(this.historyPeriod) || this.historyPeriod.length !== 2) return null;
      return HISTORY_DATE_SHORTCUTS.find((shortcut) => {
        const [start, end] = this.historyShortcutRange(shortcut.key);
        return this.historyPeriod[0] === start && this.historyPeriod[1] === end;
      })?.key || null;
    },
    HISTORY_DATE_SHORTCUTS: () => HISTORY_DATE_SHORTCUTS,
    policyFormIsValid() {
      return this.thresholdMinutes(this.policyForm.offline_threshold)
        > this.thresholdMinutes(this.policyForm.online_threshold);
    },
    policyPreview() {
      if (!this.policyFormIsValid) {
        return "O limite para offline deve ser maior que o limite para online.";
      }
      return `Verde até ${this.thresholdLabel(this.policyForm.online_threshold)}; `
        + `amarelo até ${this.thresholdLabel(this.policyForm.offline_threshold)}; `
        + "vermelho após esse período.";
    },
  },
  watch: {
    "clientViewState.selectedClient"() {
      this.selectedClient = "";
      this.page = 1;
      this.fetchDevices();
    },
  },
  methods: {
    healthLabel(status) {
      return { online: "Online", warning: "Atenção", offline: "Offline" }[status] || status;
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "—";
      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      }).format(date);
    },
    formatValue(value, suffix = "") {
      if (value === undefined || value === null || value === "") return "—";
      if (typeof value === "boolean") return value ? "Sim" : "Não";
      return `${value}${suffix}`;
    },
    formatJson(value) {
      return JSON.stringify(value || {}, null, 2);
    },
    thresholdMinutes(threshold) {
      const multipliers = { minute: 1, hour: 60, day: 1440 };
      return Number(threshold?.value || 0) * (multipliers[threshold?.unit] || 0);
    },
    thresholdLabel(threshold) {
      const value = Number(threshold?.value || 0);
      const labels = {
        minute: value === 1 ? "1 minuto" : `${value} minutos`,
        hour: value === 1 ? "1 hora" : `${value} horas`,
        day: value === 1 ? "1 dia" : `${value} dias`,
      };
      return labels[threshold?.unit] || "não informado";
    },
    policyDescription(policy) {
      if (!policy) return "padrão de 1 hora / 1 dia";
      return `online até ${this.thresholdLabel(policy.online_threshold)} e `
        + `offline após ${this.thresholdLabel(policy.offline_threshold)}`;
    },
    signalLabel(device) {
      const signal = device.status?.signal || {};
      if (signal.has_signal === false || signal.has_lock === false) return "Sem sinal";
      if (signal.cnr_db !== undefined && signal.cnr_db !== null) return `CNR ${signal.cnr_db} dB`;
      if (signal.has_signal === true || signal.has_lock === true) return "Sinal ativo";
      return "Sinal n/d";
    },
    downloadLabel(device) {
      const dsmcc = device.status?.dsmcc || {};
      if (dsmcc.mode === "MONITORING") return "Monitoring";
      if (dsmcc.download_complete) return "Concluído";
      if (dsmcc.download_percentage !== undefined) return `${dsmcc.download_percentage}% download`;
      return "Download n/d";
    },
    historySignalLabel(row) {
      return signalLabelForHistory(row);
    },
    historyConnectionState(row) {
      return connectionStateForHistory(row);
    },
    historyConnectionLabel(row) {
      return connectionLabelForHistory(row);
    },
    historyRecordTypeLabel(row) {
      return recordTypeLabelForHistory(row);
    },
    historyIsEvent(row) {
      return isHistoryEvent(row);
    },
    historyEventLabel(row) {
      return eventLabelForHistory(row);
    },
    historyMessageLabel(row) {
      return messageLabelForHistory(row);
    },
    openHistoryBody(row) {
      this.selectedHistoryBody = row;
      this.historyBodyOpen = true;
    },
    signalDetails(device) {
      const signal = device.status?.signal || {};
      return [
        { label: "CNR", value: this.formatValue(signal.cnr_db, " dB") },
        { label: "Lock", value: this.formatValue(signal.has_lock) },
        { label: "Frequência", value: this.formatValue(signal.frequency_mhz, " MHz") },
        { label: "Força", value: this.formatValue(signal.rf_signal_dbm, " dBm") },
        { label: "Erros de continuidade", value: this.formatValue(signal.continuity_errors) },
      ];
    },
    dsmccDetails(device) {
      const dsmcc = device.status?.dsmcc || {};
      return [
        { label: "Modo", value: this.formatValue(dsmcc.mode) },
        { label: "PID", value: this.formatValue(dsmcc.pid) },
        { label: "Janela", value: this.formatValue(dsmcc.monitored_tc_download_window) },
        { label: "Cenário", value: this.formatValue(dsmcc.monitored_tc_download_scenario) },
        { label: "Download", value: this.formatValue(dsmcc.download_percentage, "%") },
        { label: "Concluído", value: this.formatValue(dsmcc.download_complete) },
      ];
    },
    async fetchBusinessClients() {
      try {
        this.availableClients = await loadBusinessClients();
      } catch (error) {
        console.error("Erro ao buscar empresas autorizadas:", error);
      }
    },
    async fetchDevices() {
      this.loading = true;
      this.error = null;
      try {
        const params = { page: this.page, page_size: this.pageSize };
        if (this.activeBusinessClient) params.business_client = this.activeBusinessClient;
        if (this.appliedSearch) params.search = this.appliedSearch;
        const { data } = await axios.get(`${config.API}/v1/returnchannel/device-status`, { params });
        this.devices = data.items || [];
        this.total = data.total || 0;
      } catch (error) {
        console.error("Erro ao buscar dispositivos:", error);
        this.error = "Não foi possível carregar os dispositivos. Tente novamente.";
      } finally {
        this.loading = false;
      }
    },
    applySearch() {
      this.appliedSearch = this.search.trim();
      this.page = 1;
      this.fetchDevices();
    },
    handleFilterChange() {
      this.page = 1;
      this.fetchDevices();
    },
    handlePageChange(page) {
      this.page = page;
      this.fetchDevices();
    },
    async openPolicyDialog() {
      if (!this.policyTargetBusinessClient) return;
      this.policyDialogOpen = true;
      this.policyLoading = true;
      try {
        const { data } = await axios.get(
          `${config.API}/v1/returnchannel/device-status/policy`,
          { params: { business_client: this.policyTargetBusinessClient } },
        );
        this.policyForm = {
          online_threshold: { ...data.online_threshold },
          offline_threshold: { ...data.offline_threshold },
        };
        this.policyIsDefault = data.is_default;
      } catch (error) {
        console.error("Erro ao buscar critérios de status:", error);
        this.$message.error("Não foi possível carregar os critérios da empresa.");
        this.policyDialogOpen = false;
      } finally {
        this.policyLoading = false;
      }
    },
    async savePolicy() {
      if (!this.policyFormIsValid || !this.policyTargetBusinessClient) return;
      this.policySaving = true;
      try {
        await axios.put(
          `${config.API}/v1/returnchannel/device-status/policy`,
          this.policyForm,
          { params: { business_client: this.policyTargetBusinessClient } },
        );
        this.$message.success("Critérios atualizados para a empresa.");
        this.policyDialogOpen = false;
        await this.fetchDevices();
      } catch (error) {
        console.error("Erro ao salvar critérios de status:", error);
        const detail = error.response?.data?.detail;
        this.$message.error(detail || "Não foi possível salvar os critérios.");
      } finally {
        this.policySaving = false;
      }
    },
    async resetPolicy() {
      if (!this.policyTargetBusinessClient) return;
      try {
        await ElMessageBox.confirm(
          "A empresa voltará a usar o padrão: online até 1 hora e offline após 1 dia.",
          "Restaurar critérios padrão",
          { confirmButtonText: "Restaurar", cancelButtonText: "Cancelar", type: "warning" },
        );
      } catch {
        return;
      }
      this.policySaving = true;
      try {
        await axios.delete(
          `${config.API}/v1/returnchannel/device-status/policy`,
          { params: { business_client: this.policyTargetBusinessClient } },
        );
        this.$message.success("Critérios padrão restaurados.");
        this.policyDialogOpen = false;
        await this.fetchDevices();
      } catch (error) {
        console.error("Erro ao restaurar critérios de status:", error);
        this.$message.error("Não foi possível restaurar os critérios.");
      } finally {
        this.policySaving = false;
      }
    },
    openDevice(device) {
      this.selectedDevice = device;
      this.drawerOpen = true;
      this.history = [];
      this.historyTotal = 0;
      this.historyPage = 1;
      this.historyPeriod = [];
      this.fetchHistory();
    },
    closeDevice() {
      this.selectedDevice = null;
      this.history = [];
      this.historyPeriod = [];
      this.historyBodyOpen = false;
      this.selectedHistoryBody = null;
    },
    historyPeriodParams() {
      if (!Array.isArray(this.historyPeriod) || this.historyPeriod.length !== 2) return {};
      const [start, end] = this.historyPeriod;
      return {
        date_from: new Date(`${start}T00:00:00`).toISOString(),
        date_to: new Date(`${end}T23:59:59.999`).toISOString(),
      };
    },
    historyShortcutRange(key) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let start = new Date(today);
      if (key === "month") {
        start = new Date(today.getFullYear(), today.getMonth(), 1);
      } else if (key === "last30") {
        start.setDate(start.getDate() - 29);
      } else if (key === "year") {
        start = new Date(today.getFullYear(), 0, 1);
      }
      return [localDateOnly(start), localDateOnly(today)];
    },
    applyHistoryShortcut(key) {
      this.historyPeriod = this.historyShortcutRange(key);
      this.historyPage = 1;
      this.fetchHistory();
    },
    clearHistoryPeriod() {
      this.historyPeriod = [];
      this.historyPage = 1;
      this.fetchHistory();
    },
    async fetchHistory() {
      if (!this.selectedDevice) return;
      this.historyLoading = true;
      try {
        const deviceId = encodeURIComponent(this.selectedDevice.deviceID);
        const { data } = await axios.get(
          `${config.API}/v1/returnchannel/device-status/${deviceId}/history`,
          {
            params: {
              page: this.historyPage,
              page_size: this.historyPageSize,
              ...this.historyPeriodParams(),
            },
          },
        );
        this.history = data.items || [];
        this.historyTotal = data.total || 0;
      } catch (error) {
        console.error("Erro ao buscar histórico do device:", error);
        this.$message.error("Não foi possível carregar o histórico do dispositivo.");
      } finally {
        this.historyLoading = false;
      }
    },
    async fetchAllHistory(deviceId) {
      const pageSize = 100;
      const items = [];
      let page = 1;
      let total = 0;
      do {
        const { data } = await axios.get(
          `${config.API}/v1/returnchannel/device-status/${encodeURIComponent(deviceId)}/history`,
          {
            params: {
              page,
              page_size: pageSize,
              ...this.historyPeriodParams(),
            },
          },
        );
        const pageItems = data.items || [];
        items.push(...pageItems);
        total = data.total || items.length;
        if (pageItems.length === 0) break;
        page += 1;
      } while (items.length < total);
      return items;
    },
    async exportHistory(format) {
      if (!this.selectedDevice || this.historyExporting) return;
      const deviceId = this.selectedDevice.deviceID;
      this.historyExporting = format;
      try {
        const completeHistory = await this.fetchAllHistory(deviceId);
        if (completeHistory.length === 0) {
          this.$message.warning("Não há dados de histórico para exportar.");
          return;
        }
        if (format === "xlsx") {
          await exportDeviceHistoryExcel(deviceId, completeHistory);
        } else {
          await exportDeviceHistoryPdf(deviceId, completeHistory);
        }
        this.$message.success(`${completeHistory.length} registros exportados.`);
      } catch (error) {
        console.error("Erro ao exportar histórico do device:", error);
        this.$message.error("Não foi possível exportar o histórico do dispositivo.");
      } finally {
        this.historyExporting = null;
      }
    },
    handleHistoryPageChange(page) {
      this.historyPage = page;
      this.fetchHistory();
    },
    handleHistoryPeriodChange() {
      this.historyPage = 1;
      this.fetchHistory();
    },
    updateViewportWidth() {
      this.viewportWidth = window.innerWidth;
    },
  },
  async mounted() {
    window.addEventListener("resize", this.updateViewportWidth);
    await this.fetchBusinessClients();
    await this.fetchDevices();
    this.refreshTimer = window.setInterval(() => this.fetchDevices(), 60000);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateViewportWidth);
    if (this.refreshTimer) window.clearInterval(this.refreshTimer);
  },
};
</script>

<style scoped>
@import "@/assets/css/fonts.css";

.devices-page { font-family: "Globo-Regular", sans-serif; color: #25384a; }
.page-header, .toolbar, .pagination-row, .history-header, .history-pagination { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.page-header { margin-bottom: 24px; }
.page-header h1 { margin: 0; font: 28px "Globo-Bold", sans-serif; color: #17324d; }
.page-header p, .history-header p { margin: 6px 0 0; color: #6d7d8b; }
.header-actions { display: flex; align-items: center; gap: 10px; }
.policy-button { border-color: #9eb7c9; color: #244a68; background: #f5f9fb; }
.policy-button .el-icon { margin-right: 7px; }
.toolbar { justify-content: flex-start; flex-wrap: wrap; background: #fff; border: 1px solid #e2e9ef; border-radius: 12px; padding: 14px 16px; margin-bottom: 22px; }
.search-input { width: min(360px, 100%); }
.client-filter { width: 230px; }
.legend { margin-left: auto; display: flex; gap: 18px; color: #60717f; font-size: 13px; }
.legend span, .health-badge, .connection-label { display: inline-flex; align-items: center; gap: 7px; }
.status-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; flex: 0 0 auto; }
.status-dot.online { background: #25a767; box-shadow: 0 0 0 3px #dcf5e8; }
.status-dot.warning { background: #e2a21a; box-shadow: 0 0 0 3px #fff2d1; }
.status-dot.offline { background: #dc4c4c; box-shadow: 0 0 0 3px #fde2e2; }
.page-alert { margin-bottom: 18px; }
.devices-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(310px, 1fr)); gap: 18px; }
.skeleton-card { box-sizing: border-box; min-height: 230px; padding: 24px; background: #fff; border: 1px solid #e3eaf0; border-radius: 14px; }
.device-card { position: relative; overflow: hidden; display: flex; flex-direction: column; min-height: 238px; padding: 0; text-align: left; font: inherit; color: inherit; background: #fff; border: 1px solid #dfe7ed; border-radius: 14px; cursor: pointer; box-shadow: 0 4px 14px rgba(26, 55, 78, .045); transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
.device-card:hover, .device-card:focus-visible { transform: translateY(-2px); border-color: #a9c0d1; box-shadow: 0 10px 24px rgba(26, 55, 78, .1); outline: none; }
.card-accent { height: 5px; width: 100%; background: #25a767; }
.device-warning .card-accent { background: #e2a21a; }
.device-offline .card-accent { background: #dc4c4c; }
.card-header { display: flex; align-items: center; gap: 12px; width: 100%; box-sizing: border-box; padding: 18px 18px 14px; }
.device-icon { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 10px; color: #315d7a; background: #eaf2f7; flex: 0 0 auto; }
.device-heading { min-width: 0; display: flex; flex: 1; flex-direction: column; gap: 4px; }
.device-heading strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #17324d; font-family: "Globo-Bold", sans-serif; }
.device-heading small { color: #748491; text-transform: capitalize; }
.health-badge { padding: 6px 9px; border-radius: 999px; font-size: 12px; color: #187448; background: #edf9f3; }
.health-badge.warning { color: #93660b; background: #fff6df; }
.health-badge.offline { color: #a63838; background: #fff0f0; }
.card-body { display: flex; flex-direction: column; gap: 10px; padding: 0 18px 17px; }
.metric-row { display: flex; justify-content: space-between; gap: 14px; font-size: 14px; }
.metric-row strong { color: #344e62; font-weight: 600; text-align: right; }
.metric-label { color: #758590; }
.telemetry-row { display: flex; gap: 9px; margin-top: 4px; flex-wrap: wrap; }
.telemetry-row span { padding: 6px 9px; border-radius: 7px; color: #516a7d; background: #f3f7f9; font-size: 12px; }
.card-footer { display: flex; justify-content: space-between; gap: 12px; margin-top: auto; padding: 13px 18px; color: #738490; background: #f8fafb; border-top: 1px solid #edf1f4; font-size: 12px; }
.card-footer strong { color: #315d7a; white-space: nowrap; }
.pagination-row { margin-top: 24px; color: #687986; font-size: 13px; }
.drawer-title { width: 100%; display: flex; align-items: center; gap: 12px; padding-right: 10px; }
.drawer-title div { min-width: 0; flex: 1; }
.drawer-title h2 { overflow: hidden; text-overflow: ellipsis; margin: 0 0 4px; color: #17324d; font: 20px "Globo-Bold", sans-serif; }
.drawer-title div > span { color: #71818e; text-transform: capitalize; font-size: 13px; }
.drawer-content { padding-bottom: 30px; }
.overview-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.overview-grid > div { display: flex; flex-direction: column; gap: 7px; padding: 14px; border: 1px solid #e3eaf0; border-radius: 10px; background: #f9fbfc; }
.overview-grid span { color: #748590; font-size: 12px; }
.overview-grid strong { color: #2b465a; font-size: 14px; }
.applied-policy { display: flex; align-items: center; gap: 9px; margin-top: 14px; padding: 12px 14px; border-radius: 9px; color: #547083; background: #eef5f8; font-size: 13px; }
.applied-policy > svg { color: #315d7a; }
.applied-policy span { flex: 1; }
.applied-policy strong { color: #294b63; font-weight: 600; }
.last-event-summary { display: grid; grid-template-columns: minmax(180px, 1fr) minmax(180px, 1fr); gap: 12px 20px; margin-top: 12px; padding: 14px 16px; border: 1px solid #f0d9a8; border-radius: 9px; background: #fffaf0; }
.last-event-summary div { display: flex; flex-direction: column; gap: 4px; }
.last-event-summary span { color: #7a6b4c; font-size: 12px; }
.last-event-summary strong { color: #5d4a25; font-size: 14px; }
.last-event-summary p { grid-column: 1 / -1; margin: 0; color: #685a3d; font-size: 13px; }
.detail-sections { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; margin-top: 18px; }
.detail-panel { padding: 17px; border: 1px solid #e2e9ef; border-radius: 11px; }
.detail-panel h3 { margin: 0 0 15px; color: #244d68; font: 16px "Globo-Bold", sans-serif; }
.detail-panel h3 svg { margin-right: 7px; }
.detail-panel dl { margin: 0; font-size: 13px; }
.detail-row { display: grid; grid-template-columns: 1fr auto; gap: 16px; margin-top: 10px; }
.detail-row:first-child { margin-top: 0; }
.detail-panel dt { color: #748590; }
.detail-panel dd { margin: 0; color: #334e62; font-weight: 600; text-align: right; }
.raw-status { margin-top: 16px; }
pre { overflow: auto; max-height: 360px; margin: 0; padding: 14px; border-radius: 8px; background: #102a40; color: #d9e8f3; font: 12px/1.55 Consolas, monospace; white-space: pre-wrap; word-break: break-word; }
.history-header { margin: 28px 0 14px; }
.history-header h3 { margin: 0; color: #17324d; font: 18px "Globo-Bold", sans-serif; }
.history-header p { font-size: 13px; }
.history-table { width: 100%; }
.connection-label.connected { color: #198553; }
.connection-label.disconnected { color: #b13e3e; }
.history-actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
.history-actions svg { margin-right: 7px; }
.history-toolbar { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 14px; margin: 0 0 14px; padding: 12px; border: 1px solid #e2e9ef; border-radius: 9px; background: #f8fafb; }
.history-filter-group { display: flex; flex-direction: column; gap: 6px; }
.history-filter-label { color: #2b4054; font-size: 13px; }
.history-date-shortcuts { display: flex; align-items: center; gap: 6px; min-height: 32px; flex-wrap: wrap; }
.history-shortcut-btn { height: 32px; padding: 0 10px; border: 1px solid #dcdfe6; border-radius: 4px; background: #fff; color: #3a6d8c; font: 12px "Globo-Regular", sans-serif; cursor: pointer; white-space: nowrap; }
.history-shortcut-btn:hover { border-color: #3a6d8c; background: #f0f5f8; }
.history-shortcut-btn--active, .history-shortcut-btn--active:hover { border-color: #3a6d8c; background: #3a6d8c; color: #fff; }
.history-clear-filter { height: 32px; padding: 0 4px; border: none; background: transparent; color: #6f7b87; font: 12px "Globo-Regular", sans-serif; cursor: pointer; text-decoration: underline; }
.history-clear-filter:hover { color: #3a6d8c; }
.history-filter-help { flex-basis: 100%; color: #71818e; font-size: 12px; }
.history-pagination { margin-top: 16px; color: #6c7d89; font-size: 12px; }
.policy-intro { margin: 0 0 20px; color: #647784; line-height: 1.5; }
.policy-intro code { padding: 2px 5px; border-radius: 4px; color: #a43b3b; background: #fff0f0; }
.threshold-control { display: grid; grid-template-columns: minmax(140px, 1fr) minmax(150px, 1fr); gap: 10px; width: 100%; }
.threshold-control :deep(.el-input-number), .threshold-control :deep(.el-select) { width: 100%; }
.policy-footer { display: flex; align-items: center; justify-content: space-between; gap: 14px; width: 100%; }
.policy-footer-actions { display: flex; gap: 8px; margin-left: auto; }

@media (max-width: 760px) {
  .page-header { align-items: flex-start; flex-direction: column; }
  .header-actions { width: 100%; flex-wrap: wrap; }
  .legend { width: 100%; margin-left: 0; }
  .devices-grid, .detail-sections, .overview-grid { grid-template-columns: 1fr; }
  .last-event-summary { grid-template-columns: 1fr; }
  .last-event-summary p { grid-column: auto; }
  .pagination-row, .history-pagination { align-items: flex-start; flex-direction: column; }
  .history-header { align-items: flex-start; flex-direction: column; }
  .history-actions { width: 100%; justify-content: flex-start; }
  .history-toolbar { align-items: flex-start; flex-direction: column; }
  .threshold-control { grid-template-columns: 1fr; }
  .policy-footer { align-items: stretch; flex-direction: column; }
  .policy-footer-actions { width: 100%; margin-left: 0; }
}
</style>
